import { FC, useRef, useState } from "react";
import styled, { css } from "styled-components";

import Text from "@/components/Text";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { ProductionSceneResource } from "@/types/resources";
import { scrollBarStyles } from "@/utils/theme";

const SceneButton = styled.button<{ $dropdownOpen?: boolean }>`
  display: flex;
  height: 40px;
  align-items: center;
  padding: 0 16px;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  width: 336px;
  justify-content: space-between;
  svg {
    flex-shrink: 0;
    margin-left: 8px;
    transition: transform ease-out 0.2s;
  }
  ${({ $dropdownOpen }) =>
    $dropdownOpen &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const Holder = styled.div`
  position: relative;
`;

const SceneNumber = styled(Text)`
  width: 16px;
  height: 16px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  flex-shrink: 0;
`;

const Buttonleft = styled.div`
  flex-shrink: 0;
  display: flex;
  width: calc(100% - 20px);
  overflow: hidden;
`;

const StyledText = styled(Text)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  transition: color ease-out 0.2s;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 4px;
  max-height: 208px;
  overflow-y: auto;
  left: 0;
  width: 376px;
  z-index: 1;
  ${scrollBarStyles};
`;

const DropdownButton = styled.button<{ $active?: boolean }>`
  padding: 0 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  display: flex;
  height: 40px;
  align-items: center;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${StyledText} {
      color: ${({ theme }) => theme.colors.black};
    }
  }
  ${({ theme, $active }) =>
    $active &&
    css`
      background-color: ${theme.colors.grayscaleLight};
      ${StyledText} {
        color: ${({ theme }) => theme.colors.black};
      }
    `}
`;

interface Props {
  scenes: ProductionSceneResource[];
  activeScene: ProductionSceneResource;
  setActiveSceneId: (sceneId: number | null) => void;
}

const ScenesDropdown: FC<Props> = ({
  scenes,
  activeScene,
  setActiveSceneId,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowDropdown(false));

  const sceneNumber = scenes.findIndex((s) => s.id === activeScene.id) + 1;

  return (
    <Holder ref={ref}>
      <SceneButton
        onClick={() => setShowDropdown((prev) => !prev)}
        $dropdownOpen={showDropdown}
      >
        <Buttonleft>
          <SceneNumber $type="extraSmall">{sceneNumber}</SceneNumber>
          <StyledText $type="smallHeadline">{activeScene.name}</StyledText>
        </Buttonleft>
        <svg
          width="8"
          height="6"
          viewBox="0 0 8 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="#222222" />
        </svg>
      </SceneButton>
      {showDropdown && (
        <Dropdown>
          {scenes.map((scene, index) => (
            <DropdownButton
              $active={activeScene.id === scene.id}
              key={scene.id}
              onClick={() => {
                setActiveSceneId(scene.id);
                setShowDropdown(false);
              }}
            >
              <SceneNumber $type="extraSmall">{index + 1}</SceneNumber>
              <StyledText $type="smallHeadline" $color="gray">
                {scene.name}
              </StyledText>
            </DropdownButton>
          ))}
        </Dropdown>
      )}
    </Holder>
  );
};

export default ScenesDropdown;
